import { useConfigurationBranch } from "./useConfigurationBranch"
import { isAdminPortal } from "@/utilities/domain"
import { zodResolver } from "@hookform/resolvers/zod"
import { ApiResponse, useCustomForm } from "./useCustomForm"
import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useCrudContext } from "@/context/crudContext"
import { toast } from "sonner"
import { ZodSchema, ZodTypeDef } from "zod"

export const useAddModel = <T,>(
  url: string,
  schema: ZodSchema<T, ZodTypeDef, unknown>,
  revalidateQuery?: string,
  onClose?: () => void,
  transformData?: (data: T) => T
) => {
  const scopedReference = [
    "supplier",
    "brand",
    "discount",
    "location",
    "delivery-fee",
    "settings",
  ].includes(url)
  const { branchId, branchSelector } = useConfigurationBranch(scopedReference)
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow, setSelectedRow } =
    useCrudContext()
  const [newRecord, setNewRecord] = useState<T | null>(null)

  const defaultOnClose = () => setOpenDialog(false)

  const {
    errors,
    control,
    isSubmitting,
    Controller,
    api,
    register,
    handleSubmit,
    reset,
    setValue,
  } = useCustomForm({
    resolver: zodResolver(schema),
    defaultValues: (selectedRow ?? {}) as T,
  })

  // The modal can be reopened for another row without remounting. Keep its
  // fields in sync with the selected record instead of relying on unrelated
  // hard-coded field names such as "name" or "phone".
  useEffect(() => {
    reset((selectedRow ?? {}) as Record<string, any>)
  }, [reset, selectedRow])

  // Helper function to set form values
  const setFormValues = (data?: T) => {
    if (data) {
      Object.keys(data).forEach((key) => {
        setValue(key as keyof T & string, data[key as keyof T] as any) // Narrow key to string type
      })
    } else {
      reset()
    }
  }

  const onSubmit = async (data: any) => {
    const method = selectedRow ? "PUT" : "POST"
    const apiUrl = selectedRow ? `${url}/${selectedRow.id}` : url

    const processedData = transformData ? transformData(data) : data
    if (scopedReference && isAdminPortal() && !selectedRow) {
      processedData.branch_id = branchId
    }

    const response = await api<ApiResponse<T>>(apiUrl, {
      method,
      body: JSON.stringify(processedData),
    })

    if (response) {
      setNewRecord(response.data as T)
      console.log("test", revalidateQuery)
      queryClient.invalidateQueries({
        queryKey: revalidateQuery ? [revalidateQuery] : [url],
      })
      toast.success(`Record successfully ${selectedRow ? "updated" : "added"}`)
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  const handleReset = () => {
    setFormValues(selectedRow as T)
  }

  return {
    branchId: selectedRow?.branch_id ?? branchId,
    branchSelector: scopedReference && !selectedRow ? branchSelector : null,
    errors,
    isSubmitting,
    openDialog,
    selectedRow,
    queryClient,
    control,
    newRecord,
    setNewRecord,
    Controller,
    register,
    handleSubmit,
    onSubmit,
    handleReset,
    defaultOnClose,
    setOpenDialog,
    setSelectedRow,
    setValue,
    api,
    reset,
  }
}
